import type { QueryClient } from '@tanstack/react-query';
import type { CorSelo } from '../../componentes/base';

export type TipoDocumento = 'fatura' | 'conta_pagar' | 'nota_credito' | 'reembolso';
export type EstadoDocumento = 'em_aberto' | 'parcial' | 'pago' | 'cancelado' | 'perdido' | 'renegociado';
export type Periodicidade = 'mensal' | 'trimestral' | 'anual';

/** Fase 2 (W28): formas da cobrança no Asaas (indefinida = o cliente escolhe na fatura do Asaas). */
export const FORMAS_COBRANCA = ['pix', 'boleto', 'indefinida', 'cartao'] as const;
export type FormaCobranca = (typeof FORMAS_COBRANCA)[number];

export interface CobrancaDoc {
  /**
   * Fase 2 (W28): `cancelada` = removida ou estornada no Asaas com o documento em aberto (pode gerar outra). Review r18
   * robustez (M3): `agendada` = automática da fatura com emissão futura, pedida no dia da emissão.
   */
  estado: 'gerando' | 'gerada' | 'erro' | 'cancelada' | 'agendada';
  forma?: FormaCobranca;
  status?: string;
  aviso?: string | null;
  automatica?: boolean;
  ambiente?: 'sandbox' | 'producao';
  payment_id?: string;
  link?: string | null;
  boleto_url?: string | null;
  linha_digitavel?: string | null;
  pix_copia_e_cola?: string | null;
  pix_expira_em?: string | null;
  erro?: string | null;
  solicitada_em?: string;
  /** Review r18 robustez (M3): dia da emissão em que a cobrança agendada é pedida. */
  agendada_para?: string;
  /** Review r18 robustez (M2): falha passageira do Asaas; quando o MalgoOS tenta de novo sozinho. */
  retentar_em?: string | null;
  /** Review r49 robustez (M1): última tentativa do agendado do Sicredi (na agendada que falhou, o motivo fica em `erro`). */
  tentativa_em?: string | null;
  /** Review r51 robustez (B2): hora da falha da emissão agendada (a faixa mostra esta, não a marca do agendado). */
  erro_em?: string | null;
  /** Trilha 2 (Pix Sicredi): quem gerou a cobrança (ausente = Asaas). */
  provedor?: 'asaas' | 'sicredi';
  /** Trilha 2 (Pix Sicredi): `cob` (imediata, com validade) ou `cobv` (com vencimento). */
  pix_tipo?: 'cob' | 'cobv';
  gerada_em?: string;
}

/**
 * Review r46 (ROB-M1, SPEC-M3): o Pix do Sicredi venceu sem pagamento (a `cob` passou da validade; a `cobv`, de 60 dias depois
 * do vencimento). A mesma regra do servidor (`modules/contas/src/sicredi/validade.ts`): a tela não mostra o copia e cola e
 * libera "Gerar cobrança".
 */
export function pixSicrediVencido(c: CobrancaDoc | null, vencimento: string, hoje: string, agora = Date.now()): boolean {
  if (!c || c.provedor !== 'sicredi' || c.estado !== 'gerada' || !c.payment_id) return false;
  if (c.pix_tipo === 'cobv') {
    const limite = new Date(`${vencimento}T12:00:00Z`);
    limite.setUTCDate(limite.getUTCDate() + 60);
    return limite.toISOString().slice(0, 10) < hoje;
  }
  const expira = c.pix_expira_em ? Date.parse(c.pix_expira_em) : c.gerada_em ? Date.parse(c.gerada_em) + 3 * 86_400_000 : NaN;
  return Number.isFinite(expira) && expira <= agora;
}

/** Trilha 2 (Pix Sicredi): provedor de cobrança da regra (contrato > cliente > empresa > workspace). */
export const PROVEDORES_COBRANCA = ['asaas', 'sicredi', 'nenhum'] as const;
export type ProvedorCobranca = (typeof PROVEDORES_COBRANCA)[number];

/** `GET /contas/cobranca/provedor`. */
export interface ConfigProvedor {
  provedor_padrao: ProvedorCobranca;
  sicredi_conta_id: string | null;
  empresa_id: string | null;
  provedor_empresa: ProvedorCobranca | null;
  pode_padrao: boolean;
  asaas_conectado: boolean;
  sicredi_conectado: boolean;
  sicredi_ambiente: 'homologacao' | 'producao' | null;
  /** Review r46 (VIS-B1): o conector Pix Sicredi que vale aqui é o próprio da empresa (não o de todas). */
  sicredi_da_empresa?: boolean;
}

/** Fase 2 (W28): `GET /contas/asaas`. */
export interface ConfigAsaas {
  automatica: boolean;
  forma: FormaCobranca;
  conta_id: string | null;
  conectado: boolean;
  ambiente: 'sandbox' | 'producao' | null;
}

export interface Documento {
  id: string;
  tipo: TipoDocumento;
  direcao: 'receber' | 'pagar';
  numero: string;
  parceiro_id: string;
  parceiro_nome: string;
  descricao: string;
  emissao: string;
  vencimento: string;
  condicao_pagamento: string;
  parcela: number;
  parcelas: number;
  grupo_id: string | null;
  valor_centavos: number;
  desconto_centavos: number;
  saldo_centavos: number;
  moeda: string;
  juros_mes_pct: number;
  multa_pct: number;
  estado: EstadoDocumento;
  atrasado: boolean;
  dias_atraso: number;
  recorrencia_id: string | null;
  cobranca: CobrancaDoc | null;
  pago_em: string | null;
  origem: { modulo: string; entidade: string; id: string } | null;
  extras: Record<string, unknown>;
  created_at: string;
  /** Fase 2, W7: competência, centro, perda e chave externa. */
  competencia?: string;
  centro_custo_id?: string | null;
  centro_custo_nome?: string | null;
  /** Rateio por centro de custo (soma 100%), no lugar do centro único. */
  rateio?: { centro_custo_id: string; percentual: number }[] | null;
  chave_externa?: string | null;
  perdido_em?: string | null;
  motivo_perda?: string | null;
  /** Fase 2, W14 (F6): renegociação que fechou o documento ou de que a parcela nasceu. */
  renegociacao_id?: string | null;
  renegociado_em?: string | null;
  renegociacao_origem_id?: string | null;
  /** Fase 2, W24 (K9, P4): forma de pagamento prevista (catálogo do Cadastros). */
  forma_pagamento_id?: string | null;
  forma_pagamento_nome?: string | null;
  /** Lote 09/10 (C3): conta financeira prevista (a compra manda a conta bancária dela); sugerida na baixa. */
  conta_prevista_id?: string | null;
  conta_prevista_nome?: string | null;
}

/**
 * Lote 09/10 (C3): conta que a baixa traz escolhida: a prevista no documento (se ainda está entre as ativas), senão a padrão
 * do workspace, senão a primeira. A pessoa troca à vontade.
 */
export function contaSugeridaNaBaixa(doc: Pick<Documento, 'conta_prevista_id'>, contas: { id: string; padrao?: boolean }[]): string {
  const prevista = doc.conta_prevista_id ? contas.find((c) => c.id === doc.conta_prevista_id) : undefined;
  return (prevista ?? contas.find((c) => c.padrao) ?? contas[0])?.id ?? '';
}

export interface Item {
  descricao: string;
  quantidade: number;
  valor_unitario_centavos: number;
  valor_centavos: number;
  categoria_id: string | null;
  categoria_nome: string | null;
  centro_custo_nome?: string | null;
}

export interface Baixa {
  id: string;
  data: string;
  valor_centavos: number;
  juros_centavos: number;
  multa_centavos: number;
  desconto_centavos: number;
  total_centavos: number;
  moeda: string;
  conta_id: string | null;
  conta_nome: string | null;
  lancamento_id: string | null;
  origem: 'manual' | 'asaas' | 'sicredi';
  id_externo: string | null;
  /** Lançamento da baixa estornado no Financeiro: a baixa não vale mais. */
  estornada_em: string | null;
  /** Fase 2, W7: perda (sem caixa) e tarifa do banco. */
  tipo?: 'normal' | 'perda' | 'renegociacao';
  tarifa_centavos?: number;
  tarifa_lancamento_id?: string | null;
  motivo?: string | null;
  /** Fase 2, W24 (K9, P4): forma de pagamento usada na baixa. */
  forma_pagamento_id?: string | null;
  forma_pagamento_nome?: string | null;
}

export interface Envio {
  id: string;
  documento_id: string;
  documento_numero: string;
  parceiro_nome: string;
  canal: 'email' | 'whatsapp';
  /** Dia da régua em relação ao vencimento; nulo no envio manual (W29). */
  etapa: number | null;
  /** Fase 2 (W29): `regua` ou `manual` ("Enviar cobrança agora"). Servidor antigo não manda: vale régua. */
  origem?: 'regua' | 'manual';
  enviado_em: string;
  resultado: 'enviado' | 'falhou' | 'ignorado';
  destino: string | null;
  erro: string | null;
}

/** Fase 2 (W29): prévia do "Enviar cobrança agora" (`GET /contas/documentos/:id/enviar-cobranca`). */
export interface PreviaEnvioCobranca {
  email: string | null;
  telefone: string | null;
  /** Review r19 spec (O2): o cliente é de outra empresa, que a pessoa não vê (por isso e-mail e celular vêm nulos). */
  cliente_oculto?: boolean;
  whatsapp_disponivel: boolean;
  whatsapp_indisponivel_motivo: 'modulo' | 'conector' | null;
  cobranca_estado: CobrancaDoc['estado'] | null;
  /**
   * Review r51 (SPEC-M1, ROB-M1): a mensagem vai com um meio de pagar da cobrança viva (link, Pix ou linha digitável), de
   * qualquer provedor. Review r52 (SPEC-B1): ou com o link da fatura no Portal.
   */
  com_link: boolean;
  /** Review r52 (SPEC-B1, VIS-B2): a cobrança viva tem como pagar. Ausente (servidor anterior): vale o `com_link`. */
  com_meio_de_pagar?: boolean;
  /** Review r52 (SPEC-B1, VIS-B2): a mensagem leva o link da fatura no Portal (sem meio de pagar, só para ver a fatura). */
  com_portal?: boolean;
  /** Review r47 (SPEC-B2): o provedor da cobrança (a mensagem de "gerando" cita o certo). */
  cobranca_provedor?: 'asaas' | 'sicredi' | null;
  /** Antes da r51 (classe do R50-VIS-M1): o provedor que a regra escolhe para a cobrança que ainda vai ser gerada. */
  provedor_regra?: ProvedorCobranca;
  /** Review r47 (ROB-M2): o Pix do Sicredi venceu; a mensagem vai sem ele. `troca_erro`: `codigo: mensagem` da troca que falhou. */
  pix_vencido?: boolean;
  troca_erro?: string | null;
  asaas_conectado: boolean;
  /**
   * Review r51 (VIS-B2, SPEC-B2): o conector do provedor da regra (o da empresa da fatura) está conectado; `false` com a regra
   * "nenhum". É o que oferece "Gerar cobrança antes". Ausente (servidor anterior): o Asaas conectado, como antes.
   */
  regra_conectada?: boolean;
  espera_s: Record<'email' | 'whatsapp', number>;
  intervalo_s: number;
}

export interface EnvioManual {
  canal: 'email' | 'whatsapp';
  resultado: 'enviado' | 'falhou';
  destino: string;
  erro: string | null;
}

export interface Encargos {
  dias_atraso: number;
  juros_centavos: number;
  multa_centavos: number;
  total_centavos: number;
}

/** Fase 2, W14 (F6): a renegociação ligada ao documento, com os documentos de origem e as parcelas novas. */
export interface Renegociacao {
  id: string;
  data: string;
  motivo: string;
  moeda: string;
  saldo_centavos: number;
  juros_centavos: number;
  multa_centavos: number;
  desconto_centavos: number;
  total_centavos: number;
  parcelas: number;
  origens: { id: string; numero: string }[];
  novas: { id: string; numero: string; estado: EstadoDocumento; vencimento: string; saldo_centavos: number }[];
}

/** Fase 2, W34: aplicação de nota de crédito em fatura (compensação sem caixa), vista da nota ou da fatura. */
export interface AplicacaoCredito {
  id: string;
  lote_id: string;
  nota_id: string;
  nota_numero: string | null;
  fatura_id: string;
  fatura_numero: string | null;
  data: string;
  valor_centavos: number;
  moeda: string;
  observacao: string | null;
  created_at: string;
  desfeita_em: string | null;
  motivo_desfazer: string | null;
}

/** Fase 2, W34: fatura em que a nota pode ser aplicada (mesmo cliente, empresa e moeda). */
export interface FaturaAplicavel {
  id: string;
  numero: string;
  descricao: string;
  emissao: string;
  vencimento: string;
  parcela: number;
  parcelas: number;
  valor_centavos: number;
  saldo_centavos: number;
  moeda: string;
  estado: 'em_aberto' | 'parcial';
  atrasado: boolean;
  dias_atraso: number;
}

export interface FaturasAplicaveis {
  nota: { id: string; numero: string; parceiro_id: string; parceiro_nome: string | null; saldo_centavos: number; moeda: string; estado: EstadoDocumento };
  itens: FaturaAplicavel[];
  /** Review r24 robustez (O1): total de faturas aplicáveis (a lista traz até 200, da que vence primeiro). */
  total?: number;
}

export interface DocumentoDetalhe extends Documento {
  itens: Item[];
  baixas: Baixa[];
  envios: Envio[];
  encargos_hoje: Encargos;
  renegociacao?: Renegociacao | null;
  aplicacoes?: AplicacaoCredito[];
  /** Antes da r51 (classe do R50-VIS-M1): na fatura, o provedor que a regra de banco escolhe para a próxima cobrança. */
  provedor_regra?: ProvedorCobranca;
}

/**
 * Fase 2, W34: reparte o crédito pelas faturas na ordem em que vêm (a que vence primeiro), cada uma até o saldo dela,
 * até acabar o crédito. Devolve o valor por fatura (as que ficam sem crédito não aparecem).
 */
export function distribuirCredito(credito: number, faturas: readonly { id: string; saldo_centavos: number }[]): Record<string, number> {
  const saida: Record<string, number> = {};
  let resta = Math.max(0, credito);
  for (const f of faturas) {
    if (resta <= 0) break;
    const v = Math.min(resta, f.saldo_centavos);
    if (v > 0) {
      saida[f.id] = v;
      resta -= v;
    }
  }
  return saida;
}

export interface Totais {
  quantidade: number;
  em_aberto_centavos: number;
  atrasado_centavos: number;
  atrasados: number;
  vence_em_7_dias_centavos: number;
  pago_centavos: number;
  /** C1: os cards de A receber e A pagar. */
  vence_hoje_centavos: number;
  vence_hoje: number;
  a_vencer_centavos: number;
  a_vencer: number;
  liquidado_centavos: number;
  liquidados: number;
  total_centavos: number;
  /** Lista de 04/11, item 3: os totais em dinheiro por moeda (os campos acima são só a parte na do workspace). */
  por_moeda?: ({ moeda: string } & Partial<Record<string, number | string>>)[];
}

export interface ItemModelo {
  descricao: string;
  quantidade: number;
  valor_unitario_centavos: number;
  categoria_id?: string | null;
}

export interface Recorrencia {
  id: string;
  descricao: string;
  tipo: 'fatura' | 'conta_pagar';
  parceiro_id: string;
  parceiro_nome: string;
  periodicidade: Periodicidade;
  modelo: { itens: ItemModelo[]; descricao_documento: string | null; juros_mes_pct?: number; multa_pct?: number; desconto_centavos?: number };
  valor_centavos: number;
  moeda: string;
  dia_base: number;
  proxima_data: string;
  prazo_dias: number;
  fim: string | null;
  ativa: boolean;
  ultima_geracao: string | null;
  documentos: number;
  created_at: string;
}

export interface Regua {
  dias: number[];
  canais: ('email' | 'whatsapp')[];
  ativa: boolean;
  /** Falso sem o módulo Conversas ou sem o conector de WhatsApp conectado. */
  whatsapp_disponivel: boolean;
  whatsapp_indisponivel_motivo: 'modulo' | 'conector' | null;
}

export interface CategoriaFin {
  id: string;
  nome: string;
  tipo: 'receita' | 'despesa';
  parent_id: string | null;
  ativa: boolean;
  /** Fase 2, W24 (K9): sintética só agrupa (fora do seletor do item). */
  sintetica?: boolean;
}

export interface ContaFin {
  id: string;
  nome: string;
  ativa: boolean;
  padrao: boolean;
  moeda: string;
}

export interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

/** Depois de qualquer escrita: lista, totais, detalhe, recorrências e cobranças mudam juntos. */
export function invalidarContas(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/contas') }),
    // O lançamento da baixa aparece no Financeiro.
    qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/financeiro') }),
    qc.invalidateQueries({ queryKey: ['chatter'] }),
  ]);
}

/** Hoje no relógio do navegador (AAAA-MM-DD), sem o desvio de UTC que a noite brasileira causaria. */
export function hojeLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const COR_ESTADO: Record<EstadoDocumento, CorSelo> = { em_aberto: 'azul', parcial: 'ciano', pago: 'verde', cancelado: 'neutro', perdido: 'perigo', renegociado: 'roxo' };

export interface CentroFin {
  id: string;
  nome: string;
  ativo: boolean;
}

/** Fase 2, W7 (P5): prévia da importação de títulos. */
export interface LinhaPrevia {
  linha: number;
  chave_externa: string | null;
  estado: 'ok' | 'erro' | 'existente';
  erros: { campo: string; codigo: string; mensagem: string }[];
  sugestoes: { id: string; nome: string; documento: string | null }[];
  tipo: TipoDocumento | null;
  parceiro_id: string | null;
  parceiro_nome: string | null;
  valor_centavos: number | null;
  vencimento: string | null;
  baixa: boolean;
  perda: boolean;
}

export interface Previa {
  total: number;
  validas: number;
  com_erro: number;
  existentes: number;
  linhas: LinhaPrevia[];
}

/** Rótulo da etapa da régua: "3 dias antes", "no vencimento", "7 dias depois" (a tradução fica no chamador). */
export function tipoDaEtapa(etapa: number): 'antes' | 'no_dia' | 'depois' {
  return etapa < 0 ? 'antes' : etapa === 0 ? 'no_dia' : 'depois';
}

/** Fase 2 (W29): chave e contagem do rótulo da etapa de um envio; o envio manual não tem etapa ("Envio manual"). */
export function rotuloEtapaEnvio(e: Pick<Envio, 'etapa' | 'origem'>): [string, { count: number }] {
  if (e.origem === 'manual' || e.etapa === null) return ['etapa.manual', { count: 0 }];
  return [`etapa.${tipoDaEtapa(e.etapa)}`, { count: Math.abs(e.etapa) }];
}
